import React, { useState, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth, useSettings } from '../App'
import { BRANDS, PRODUCT_TYPES, GENDERS, EMPTY_PRODUCT } from '../lib/constants'
import {
  getLandedGBP, getOffPriceWS, getOffPriceRRP,
  getStdWS, getStdRRP, getTRSupply, getTRRRP,
  getStockTotal, getStockStatus
} from '../lib/pricing'
import ProductModal from './ProductModal'

const PAGE_SIZE = 10

export default function CatalogueTab({ products, loading, onRefresh }) {
  const { rolePerms } = useAuth()
  const { settings } = useSettings()
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterGender, setFilterGender] = useState('')
  const [activeBrand, setActiveBrand] = useState('all')
  const [sortBy, setSortBy] = useState('sku')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editProduct, setEditProduct] = useState(null)
  const [saving, setSaving] = useState(false)

  const filtered = useMemo(() => {
    let list = products
    if (activeBrand !== 'all') list = list.filter(p => p.brand === activeBrand)
    if (filterType) list = list.filter(p => p.product_type === filterType)
    if (filterGender) list = list.filter(p => p.gender === filterGender)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(p =>
        [p.sku, p.ean_code, p.brand, p.specification, p.factory_sku]
          .some(x => (x || '').toLowerCase().includes(q))
      )
    }
    return [...list].sort((a, b) => {
      if (sortBy === 'brand') return a.brand.localeCompare(b.brand)
      if (sortBy === 'price') return getStdRRP(b, 'GBP', settings) - getStdRRP(a, 'GBP', settings)
      if (sortBy === 'stock') return getStockTotal(b) - getStockTotal(a)
      return a.sku.localeCompare(b.sku)
    })
  }, [products, activeBrand, filterType, filterGender, search, sortBy, settings])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const brandCounts = useMemo(() => {
    const c = {}
    products.forEach(p => { c[p.brand] = (c[p.brand] || 0) + 1 })
    return c
  }, [products])

  const stats = useMemo(() => ({
    total: products.length,
    brands: new Set(products.map(p => p.brand)).size,
    inStock: products.filter(p => getStockTotal(p) > 0).length,
    ukTotal: products.reduce((s, p) => s + (parseInt(p.stock_uk) || 0), 0),
    deTotal: products.reduce((s, p) => s + (parseInt(p.stock_de) || 0), 0),
  }), [products])

  function openAdd() {
    setEditProduct({ ...EMPTY_PRODUCT })
    setModalOpen(true)
  }

  function openEdit(product) {
    setEditProduct({ ...product })
    setModalOpen(true)
  }

  async function handleSave(data) {
    setSaving(true)
    try {
      if (data.id) {
        const { error } = await supabase.from('products').update(data).eq('id', data.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('products').insert(data)
        if (error) throw error
      }
      setModalOpen(false)
      onRefresh()
    } catch (err) {
      alert('Error saving product: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this product? This cannot be undone.')) return
    try {
      const { error } = await supabase.from('products').delete().eq('id', id)
      if (error) throw error
      onRefresh()
    } catch (err) {
      alert('Error deleting product: ' + err.message)
    }
  }

  if (loading) return <div className="loading-pane">Loading catalogue...</div>

  return (
    <div className="tab-content">
      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card"><div className="stat-label">Products</div><div className="stat-value">{stats.total}</div></div>
        <div className="stat-card"><div className="stat-label">Brands</div><div className="stat-value">{stats.brands}</div><div className="stat-sub">of 15</div></div>
        <div className="stat-card"><div className="stat-label">In stock</div><div className="stat-value">{stats.inStock}</div><div className="stat-sub">{stats.total - stats.inStock} out</div></div>
        <div className="stat-card"><div className="stat-label">UK stock</div><div className="stat-value">{stats.ukTotal}</div><div className="stat-sub">Flostream UK</div></div>
        <div className="stat-card"><div className="stat-label">DE stock</div><div className="stat-value">{stats.deTotal}</div><div className="stat-sub">Flostream DE</div></div>
      </div>

      {/* Brand pills */}
      <div className="brand-pills">
        <button className={`pill ${activeBrand === 'all' ? 'active' : ''}`} onClick={() => { setActiveBrand('all'); setPage(1) }}>
          All ({products.length})
        </button>
        {BRANDS.filter(b => brandCounts[b]).map(b => (
          <button key={b} className={`pill ${activeBrand === b ? 'active' : ''}`} onClick={() => { setActiveBrand(b); setPage(1) }}>
            {b} ({brandCounts[b]})
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <input type="text" placeholder="Search SKU, EAN, spec..." value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }} style={{ width: 200 }} />
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1) }}>
          <option value="">All types</option>
          {PRODUCT_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={filterGender} onChange={e => { setFilterGender(e.target.value); setPage(1) }}>
          <option value="">All genders</option>
          {GENDERS.map(g => <option key={g}>{g}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="sku">Sort: SKU</option>
          <option value="brand">Sort: Brand</option>
          <option value="price">Sort: RRP</option>
          <option value="stock">Sort: Stock</option>
        </select>
        <div style={{ flex: 1 }} />
        {rolePerms.canEdit && (
          <button className="btn primary" onClick={openAdd}>+ Add product</button>
        )}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Product / SKU</th>
              <th>Brand</th>
              <th>EAN</th>
              <th>Landed GBP</th>
              <th>OP WS</th><th>OP RRP</th>
              <th>Std WS</th><th>Std RRP</th>
              <th>TR Supply</th><th>TR RRP</th>
              <th>UK</th><th>DE</th>
              {(rolePerms.canEdit || rolePerms.canDelete) && <th></th>}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr><td colSpan={13} style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-secondary)' }}>No products found</td></tr>
            ) : pageData.map(p => {
              // const status = getStockStatus(p)
              const lgbp = getLandedGBP(p, settings)
              return (
                <tr key={p.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {p.packshot_image
                        ? <img src={p.packshot_image} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                        : <div className="thumb">⌚</div>
                      }
                      <div>
                        <div className="product-name">{p.sku}</div>
                        <div className="product-sku">{(p.specification || p.product_type || '').substring(0, 32)}{p.specification?.length > 32 ? '…' : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{p.brand}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>{p.ean_code || '—'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>£{lgbp.toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>£{getOffPriceWS(p, 'GBP', settings).toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500 }}>£{getOffPriceRRP(p, 'GBP', settings)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>£{getStdWS(p, 'GBP', settings).toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500 }}>£{getStdRRP(p, 'GBP', settings)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>£{getTRSupply(p, 'GBP', settings).toFixed(2)}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 500 }}>£{getTRRRP(p, 'GBP', settings)}</td>
                  <td><span className="badge badge-uk">{p.stock_uk || 0}</span></td>
                  <td><span className="badge badge-de">{p.stock_de || 0}</span></td>
                  {(rolePerms.canEdit || rolePerms.canDelete) && (
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {rolePerms.canEdit && <button className="action-btn" onClick={() => openEdit(p)}>Edit</button>}
                      {rolePerms.canDelete && <button className="action-btn danger" onClick={() => handleDelete(p.id)}>Del</button>}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <span>{filtered.length} products · page {page} of {totalPages}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button key={n} className={`page-btn ${n === page ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
          ))}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <ProductModal
          product={editProduct}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          saving={saving}
          settings={settings}
        />
      )}
    </div>
  )
}
